import React, { useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Tooltip,
  Typography,
  Box,
  Paper,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CancelIcon from '@mui/icons-material/Cancel';
import Draggable from 'react-draggable';
import { styled } from '@mui/system';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

const StyledContainer = styled(Paper)(() => ({
  backgroundColor: 'white',
  borderRadius: '8px',
  padding: '16px',
  display: 'flex',
  flexDirection: 'column',
  boxShadow: '0px 4px 10px rgba(0,0,0,0.4)',
  border: '1px solid #304fff',
  minWidth: 360,
  maxWidth: 440,
}));

function PaperComponent(props) {
  const nodeRef = useRef(null);
  return (
    <Draggable handle=".handle" nodeRef={nodeRef} cancel={'[class*="MuiDialogContent-root"]'}>
      <StyledContainer ref={nodeRef} {...props} />
    </Draggable>
  );
}

/**
 * Shows the CaseId-derived initial client password after create (or via Show Password).
 * Password is never read from the DB hash — only regenerated/returned by the backend.
 */
export default function InitialClientPasswordModal({
  open,
  onClose,
  password,
  dir = 'rtl',
  showSuccessTitle = false,
}) {
  const { t } = useTranslation();
  const isRtl = dir === 'rtl';

  const handleCopy = async () => {
    if (!password) return;
    const toastOpts = { position: 'top-left' };
    try {
      await navigator.clipboard.writeText(String(password));
      toast.success(t('PasswordCopied'), toastOpts);
    } catch (err) {
      console.error('Failed to copy password', err);
      toast.error(t('FailedToCopyPassword'), toastOpts);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperComponent={PaperComponent}
      PaperProps={{ style: { direction: dir, zIndex: 1301 } }}
      disableEnforceFocus
      keepMounted
    >
      {/* Header — same pattern as PasswordDialog: title + close, direction from PaperProps */}
      <div
        className="handle"
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 16,
          fontWeight: 'bold',
          cursor: 'move',
          width: '100%',
        }}
      >
        <DialogTitle sx={{ p: 0, m: 0, fontWeight: 700, fontSize: '1.15rem' }}>
          {showSuccessTitle ? t('CustomerAddedSuccessfully') : t('InitialPassword')}
        </DialogTitle>
        <Tooltip title={t('Exit')}>
          <IconButton onClick={onClose} size="small">
            <CancelIcon sx={{ color: '#304FFF' }} />
          </IconButton>
        </Tooltip>
      </div>

      <DialogContent sx={{ pt: 0, px: 1, pb: 1 }}>
        {showSuccessTitle && (
          <Typography
            variant="subtitle1"
            sx={{
              mb: 2,
              fontWeight: 600,
              textAlign: isRtl ? 'left' : 'right',
            }}
          >
            {t('InitialPassword')}
          </Typography>
        )}

        {/*
          Password digits stay LTR; copy icon is placed on the visual RIGHT of the digits
          (flexDirection row + DOM order: password, then icon).
        */}
        <Box
          dir="ltr"
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
            py: 2,
            width: '100%',
          }}
        >
          <Typography
            variant="h4"
            component="span"
            sx={{
              letterSpacing: '0.35em',
              fontWeight: 700,
              fontFamily: 'monospace',
              color: '#1a237e',
              // Cancel trailing letter-spacing after the last digit so the copy icon sits tight
              marginInlineEnd: '-0.35em',
            }}
          >
            {password || '————'}
          </Typography>
          <Tooltip title={t('CopyPassword')}>
            <span>
              <IconButton
                onClick={handleCopy}
                disabled={!password}
                aria-label={t('CopyPassword')}
                sx={{ color: '#304FFF' }}
              >
                <ContentCopyIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'center', mt: 1, px: 0 }}>
        <Button
          onClick={onClose}
          variant="contained"
          sx={{
            background: '#304FFF',
            minWidth: 96,
            '&:hover': { background: '#1E40FF' },
          }}
        >
          {t('Close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
